import React, { useEffect, useState } from 'react';
import { IInvoice } from 'internal/payments/Models';
import { Grid, styled, TablePagination } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { Download } from '@mui/icons-material';
import { IconButton, OutlinedDropdown, Text } from 'library';
import { PagingResult } from 'models';
import { client, consoleLogInDev, download, formatCurrency } from 'shared';
import PaySectionHeader from './PaySectionHeader';
import { PayBackgroundPaper } from './PayPapers';
import { formatMonthDay, formatMonthDayYear } from 'shared/Dates';
import { addDays } from 'date-fns';
import { useUserContext } from 'UserContext';

interface IDueInvoice {
    due_date: string;
    is_paid: boolean;
    invoices: IInvoice[];
}

export default function DuePayments() {
    const [pageNumber, setPageNumber] = useState(1);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [dueInvoices, setDueInvoices] = useState<PagingResult<IDueInvoice>>({
        count: 0,
        per_page: 0,
        num_pages: 0,
        results: [],
    });

    const { user } = useUserContext();

    const locations = user?.address.filter((x) => x.business_location).map((x) => x.business_location);

    useEffect(() => {
        getDuePayments(pageNumber);
    }, []);

    function getDuePayments(page: number, locationId?: string) {
        const id = locationId || '';
        client(`payment/api/invoice/?type=due&page=${page}&size=5&location_id=${id}`)
            .then((response: PagingResult<IDueInvoice>) => {
                setDueInvoices(response);
            })
            .catch(consoleLogInDev);
    }

    function onDownloadClick(invoiceId: number) {
        download(`payment/api/invoice/${invoiceId}/download/`).catch((error) => {
            consoleLogInDev(error);
        });
    }

    function onPageChange(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
        const newPageNumber = newPage + 1;
        setPageNumber(newPageNumber);
        getDuePayments(newPageNumber, selectedLocation);
    }

    function onLocationChange(e: SelectChangeEvent<string | number | null>) {
        const locationValue = String(e.target.value);
        setPageNumber(1);
        setSelectedLocation(locationValue);
        getDuePayments(1, locationValue);
    }

    function getWeekText(billDate: string) {
        const parsedBillDate = new Date(billDate);
        const weekStart = addDays(parsedBillDate, -6);

        return `${formatMonthDay(weekStart)}-${formatMonthDayYear(parsedBillDate)}`;
    }

    function getCurrencyString(invoice: IInvoice, isUnpaidGroup?: boolean) {
        if (!isUnpaidGroup) {
            return formatCurrency(invoice.amount);
        }

        const amountPaid = invoice.amount_paid;
        if (amountPaid) {
            const unpaidAmount = invoice.amount - amountPaid;
            return `${formatCurrency(invoice.amount)} (${formatCurrency(unpaidAmount)} owed)`;
        }

        return formatCurrency(invoice.amount);
    }

    function renderInvoiceGroup(invoiceGroup: IDueInvoice[], isUnpaidGroup?: boolean) {
        return invoiceGroup.map((x, i) => (
            <InvoiceGroup key={i}>
                <Text>{formatMonthDayYear(x.due_date.replaceAll('-', '/'))}</Text>
                {x.invoices.map((invoice) => (
                    <InvoiceRow key={invoice.id} container alignItems="center">
                        <Grid item sm={8} xs={12}>
                            <Text variant="body2">
                                Invoice {invoice.id}, week of {getWeekText(invoice.bill_date)}
                            </Text>
                        </Grid>
                        <Grid item sm={3} xs={10}>
                            <Text variant="body2">{getCurrencyString(invoice, isUnpaidGroup)}</Text>
                        </Grid>
                        <Grid item sm={1} xs={2}>
                            <IconButton icon={Download} onClick={() => onDownloadClick(invoice.id)} />
                        </Grid>
                    </InvoiceRow>
                ))}
            </InvoiceGroup>
        ));
    }

    const unpaidGroup = dueInvoices.results.filter((x) => !x.is_paid);
    const paidGroup = dueInvoices.results.filter((x) => x.is_paid);

    return (
        <Grid item xs={12}>
            <PayBackgroundPaper>
                <PaySectionHeader>Invoices</PaySectionHeader>
                <Grid container justifyContent="flex-end">
                    <OutlinedDropdown
                        label="Location"
                        value={selectedLocation ?? ''}
                        fields={
                            locations?.map((b) => {
                                return { key: b!.id.toString(), value: b!.name };
                            }) || []
                        }
                        onChange={onLocationChange}
                    />
                </Grid>
                <Grid style={{ marginBottom: 10 }}>
                    {unpaidGroup.length ? (
                        <Text variant="body2" bold>
                            Unpaid
                        </Text>
                    ) : null}
                    {renderInvoiceGroup(unpaidGroup, true)}
                    {paidGroup.length ? (
                        <Text variant="body2" bold>
                            Paid
                        </Text>
                    ) : null}
                    {renderInvoiceGroup(paidGroup)}
                </Grid>
                <Grid>
                    <TablePagination
                        component="div"
                        count={dueInvoices.count}
                        rowsPerPage={dueInvoices.per_page}
                        page={pageNumber - 1}
                        onPageChange={onPageChange}
                        rowsPerPageOptions={[]}
                    />
                </Grid>
            </PayBackgroundPaper>
        </Grid>
    );
}

const InvoiceRow = styled(Grid)({
    padding: '5px 0px 5px 15px',
});

const InvoiceGroup = styled(Grid)({
    marginTop: 10,
});
